import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, UserPlus, Users, X } from 'lucide-react';
import { Button, Modal, ModalHeader, useToast } from './ui';
import { useAuth } from '../../Accounts/AuthContext';
import { lastNameOf } from '../data/mockData';
import {
  addCoTeacher,
  addStudents,
  deleteClass,
  fetchRoster,
  listCoTeachers,
  listSchoolTeachers,
  listStudentPool,
  notifyClassesChanged,
  removeCoTeacher,
  removeStudentFromClass,
  updateClass,
} from '../data/api';
import { writeSelectedClassId } from '../useSelectedClass';

/*
 * Manage class (2026-08-05; roster split out 2026-08-09): the class's settings
 * in one house modal, opened from the class switcher. Details (name/period,
 * save via updateClass), an "Edit students" launcher, and the danger row
 * (deleteClass, click-again confirm).
 *
 * EditStudentsModal is the roster editor built for SCALE (Sandy 2026-08-09):
 * a school can have hundreds of students, so both lists get one shared search
 * box, name/grade sort, NAMES first (email as the secondary identifier, only
 * primary when the account has no name yet), grade chips (from the pool
 * payload's UserDatabase lookup), select-all-shown, and scrollable sections.
 * While it is open the manage modal hides underneath (open && !studentsOpen)
 * so Esc/close always acts on the top-most dialog only.
 *
 * Every destructive action uses the inline click-again pattern (see
 * UnitPlan's confirmAbandon) — window.confirm never renders in embedded
 * browsers.
 */

const INPUT =
  'h-9 w-full rounded-[6px] border border-neutral-300 bg-white px-2.5 text-[13px] text-ink outline-none focus:border-primary-400';

// the selected class is per tab (useSelectedClass writeSelectedClassId, 2026-10-26)

// names with numbers sort by the number ("Sim Student 5" before "Sim Student 49"; fleet A-F17, 2026-10-26)
const NUMERIC = { numeric: true };
const gradeLabel = (g) => (g ? `${g}th` : null);
const gradeNum = (g) => { const n = Number(g); return Number.isFinite(n) && n > 0 ? n : 99; };

/* One student row shared by both lists: name first, grade chip, email as the
 * quiet secondary identifier (only when it isn't already the display name). */
function StudentRow({ s, right, children }) {
  const display = s.realName || s.name || s.email || s.userId;
  const secondary = s.email && s.email !== display ? s.email : null;
  return (
    <div className="flex items-center gap-2.5 min-w-0 flex-1">
      {children}
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold text-ink truncate leading-tight">{display}</span>
        {secondary && <span className="block text-[11px] text-neutral-400 truncate leading-tight">{secondary}</span>}
      </span>
      {gradeLabel(s.grade) && (
        <span className="flex-none text-[10.5px] font-bold text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-[4px] px-1.5 py-0.5">
          {gradeLabel(s.grade)}
        </span>
      )}
      {right}
    </div>
  );
}

/* The roster editor. Fetches fresh on every open; search + sort are shared by
 * the enrolled and available lists. */
function EditStudentsModal({ open, onClose, cls, auth, pushToast, initialQuery = '', onAction }) {
  const [roster, setRoster] = useState(null); // null = loading
  const [pool, setPool] = useState(null);
  const [scope, setScope] = useState(null); // 'school' | 'my-classes'
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'grade'
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [checked, setChecked] = useState(() => new Set());
  const [addBusy, setAddBusy] = useState(false);

  useEffect(() => {
    if (!open || !cls) return undefined;
    setQuery(initialQuery || '');
    setSortBy('name');
    setConfirmRemove(null);
    setRemovingId(null);
    setChecked(new Set());
    setAddBusy(false);
    setRoster(null);
    setPool(null);
    let alive = true;
    fetchRoster(auth, cls.id, { force: true })
      .then((r) => { if (alive) setRoster(r); })
      .catch(() => { if (alive) setRoster([]); });
    listStudentPool(auth)
      .then((p) => {
        if (!alive) return;
        setPool(Array.isArray(p) ? p : (p?.students || []));
        setScope(Array.isArray(p) ? null : (p?.scope || null));
      })
      .catch(() => { if (alive) setPool([]); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cls?.id]);

  const poolById = useMemo(() => {
    const m = new Map();
    for (const p of pool || []) m.set(p.userId, p);
    return m;
  }, [pool]);

  const q = query.trim().toLowerCase();
  const matches = (s) =>
    !q
    || String(s.realName || s.name || '').toLowerCase().includes(q)
    || String(s.email || '').toLowerCase().includes(q);
  const bySort = (a, b) => {
    const nameA = String(a.realName || a.name || a.email || '');
    const nameB = String(b.realName || b.name || b.email || '');
    if (sortBy === 'grade') {
      return gradeNum(a.grade) - gradeNum(b.grade)
        || lastNameOf(nameA).localeCompare(lastNameOf(nameB), undefined, NUMERIC) || nameA.localeCompare(nameB, undefined, NUMERIC);
    }
    return lastNameOf(nameA).localeCompare(lastNameOf(nameB), undefined, NUMERIC) || nameA.localeCompare(nameB, undefined, NUMERIC);
  };

  // Enrolled rows, enriched with the pool's grade/email when the school pool
  // knows this student (fetchRoster carries name only).
  const enrolled = useMemo(() => {
    if (roster === null) return null;
    return roster
      .map((s) => {
        const p = poolById.get(s.userId);
        return { userId: s.userId, name: s.name, realName: p?.realName || s.name, email: p?.email || null, grade: p?.grade || null };
      })
      .filter(matches)
      .sort(bySort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roster, poolById, q, sortBy]);

  const available = useMemo(() => {
    if (pool === null || roster === null) return null;
    const inClass = new Set(roster.map((s) => s.userId));
    return pool.filter((p) => !inClass.has(p.userId)).filter(matches).sort(bySort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool, roster, q, sortBy]);

  if (!cls) return null;

  const refreshRoster = async () => {
    try { setRoster(await fetchRoster(auth, cls.id, { force: true })); } catch { /* keep last */ }
  };

  const remove = async (s) => {
    if (removingId) return;
    setRemovingId(s.userId);
    try {
      await removeStudentFromClass(auth, cls.id, s.userId);
      await refreshRoster();
      notifyClassesChanged();
      onAction?.(`Removed ${s.realName || s.name || 'a student'} from the class.`);
    } catch (e) {
      pushToast(e?.viewOnly ? e.message : 'Could not remove the student');
    } finally {
      setRemovingId(null);
      setConfirmRemove(null);
    }
  };

  const toggle = (userId) => {
    setChecked((cur) => {
      const next = new Set(cur);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };
  const allShownChecked = (available || []).length > 0 && (available || []).every((p) => checked.has(p.userId));
  // Select a whole grade at once (BBO-34, 2026-10-13): one chip per grade in
  // the list below, with its count; a second click clears that grade.
  const gradeCounts = new Map();
  for (const p of available || []) if (gradeLabel(p.grade)) gradeCounts.set(String(p.grade), (gradeCounts.get(String(p.grade)) || 0) + 1);
  const gradesShown = [...gradeCounts.entries()].sort((a, b) => gradeNum(a[0]) - gradeNum(b[0]));
  const gradeAllChecked = (g) => (available || []).filter((p) => String(p.grade) === g).every((p) => checked.has(p.userId));
  const toggleGrade = (g) => {
    const inGrade = (available || []).filter((p) => String(p.grade) === g);
    const all = inGrade.every((p) => checked.has(p.userId));
    setChecked((cur) => {
      const next = new Set(cur);
      inGrade.forEach((p) => (all ? next.delete(p.userId) : next.add(p.userId)));
      return next;
    });
  };
  const toggleAllShown = () => {
    setChecked((cur) => {
      const next = new Set(cur);
      if (allShownChecked) (available || []).forEach((p) => next.delete(p.userId));
      else (available || []).forEach((p) => next.add(p.userId));
      return next;
    });
  };

  const add = async () => {
    const picked = (pool || [])
      .filter((p) => checked.has(p.userId))
      .map((p) => ({ userId: p.userId, name: p.realName || p.name || p.email || p.userId }));
    if (addBusy || picked.length === 0) return;
    setAddBusy(true);
    // The backend caps each addStudents call at 100 students, so a big
    // select-all goes up in chunks. If a chunk fails, the students not yet
    // sent stay checked so Add can be clicked again for just the rest.
    const unsent = new Set(picked.map((s) => s.userId));
    let added = 0;
    let skipped = 0;
    let blocked = 0;
    let failed = false;
    let failure = null;
    try {
      for (let i = 0; i < picked.length; i += 100) {
        const chunk = picked.slice(i, i + 100);
        const r = await addStudents(auth, cls.id, chunk);
        chunk.forEach((s) => unsent.delete(s.userId));
        added += r?.added || 0;
        skipped += r?.skipped || 0;
        blocked += r?.blocked || 0;
      }
    } catch (e) {
      failed = true;
      failure = e;
    }
    if (added || skipped || blocked) {
      const bits = [`${added} student${added === 1 ? '' : 's'} added`];
      if (skipped) bits.push(`${skipped} already enrolled`);
      if (blocked) bits.push(`${blocked} outside your school`);
      if (failed) bits.push('the rest stayed selected — click Add again');
      pushToast(bits.join(' · '));
      onAction?.(`${bits.join(', ')}.`);
      setChecked(new Set(unsent));
      await refreshRoster();
      notifyClassesChanged();
    } else {
      pushToast(failure?.viewOnly ? failure.message : 'Could not add the students');
    }
    setAddBusy(false);
  };

  return (
    <Modal open={open} onClose={onClose} size="lg">
      <ModalHeader
        title="Students"
        sub={(cls.period ? cls.period + ' · ' : '') + cls.name}
        onClose={onClose}
      />
      {/* search + sort control the whole editor */}
      <div className="flex items-center gap-2.5 px-[24px] py-[12px] border-b border-neutral-100 flex-none flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-[320px]">
          <Search size={14} strokeWidth={2.5} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or email"
            className="w-full h-9 pl-8 pr-7 text-[13px] font-semibold text-ink bg-white border-[1px] border-neutral-300 rounded-[6px] outline-none focus:border-neutral-400 placeholder:font-normal placeholder:text-neutral-400"
          />
          {query && (
            <button onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center border-none bg-transparent p-0.5 text-neutral-400 hover:text-neutral-600 cursor-pointer">
              <X size={13} strokeWidth={2.5} />
            </button>
          )}
        </div>
        <span className="text-[11px] font-semibold text-neutral-400">Sort by</span>
        <div className="flex gap-1">
          {[['name', 'Name'], ['grade', 'Grade']].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setSortBy(k)}
              className={
                'cursor-pointer text-[11px] font-bold px-2.5 py-1 rounded-[6px] border ' +
                (sortBy === k ? 'text-white bg-ink border-ink' : 'text-neutral-600 bg-white border-neutral-300 hover:bg-neutral-50')
              }
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* ---- in this class ---- */}
        <div className="px-[24px] py-[16px]">
          <div className="text-[11px] font-extrabold uppercase tracking-wide text-neutral-400 mb-1.5">
            In this class{roster !== null ? ` (${roster.length})` : ''}
          </div>
          {enrolled === null ? (
            <div className="text-[12px] text-neutral-400 py-1">Loading…</div>
          ) : enrolled.length === 0 ? (
            <div className="text-[13px] text-neutral-500 py-1">{q ? 'No enrolled students match.' : 'No students in this class yet.'}</div>
          ) : (
            <div className="max-h-[240px] overflow-y-auto border-[1px] border-neutral-200 rounded-[8px]">
              {enrolled.map((s) => (
                <div key={s.userId} className="flex items-center gap-[12px] px-[12px] py-2 border-b border-neutral-100 last:border-b-0">
                  <StudentRow s={s} />
                  <button
                    type="button"
                    onClick={confirmRemove === s.userId ? () => remove(s) : () => setConfirmRemove(s.userId)}
                    disabled={!!removingId}
                    className={
                      'flex-none text-[12px] font-semibold border-0 bg-transparent cursor-pointer p-0 disabled:opacity-60 disabled:cursor-default ' +
                      (confirmRemove === s.userId ? 'text-error-700' : 'text-neutral-400 hover:text-error-700')
                    }
                  >
                    {removingId === s.userId ? 'Removing…' : confirmRemove === s.userId ? 'Click again to confirm' : 'Remove'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ---- add from the pool ---- */}
        <div className="border-t border-neutral-100 px-[24px] py-[16px]">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="text-[11px] font-extrabold uppercase tracking-wide text-neutral-400">
              {scope === 'school' ? 'Add from your school' : 'Add students'}
              {available !== null ? ` (${available.length}${q ? ' shown' : ' available'})` : ''}
            </div>
            <span className="flex-1" />
            {(available || []).length > 1 && (
              <button
                type="button"
                onClick={toggleAllShown}
                className="text-[11.5px] font-bold text-neutral-500 hover:text-ink bg-transparent border-0 p-0 cursor-pointer"
              >
                {allShownChecked ? 'Clear shown' : `Select all${q ? ' shown' : ''}`}
              </button>
            )}
          </div>
          {gradesShown.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5 mb-2" role="group" aria-label="Select a whole grade">
              <span className="text-[11.5px] font-semibold text-neutral-500 mr-0.5">Select a grade</span>
              {gradesShown.map(([g, n]) => (
                <button key={g} type="button" aria-pressed={gradeAllChecked(g)} onClick={() => toggleGrade(g)}
                  aria-label={gradeAllChecked(g) ? `All ${gradeLabel(g)} graders selected · ${n}` : `Select all ${gradeLabel(g)} graders · ${n}`}
                  className={'h-7 px-2.5 rounded-full text-[12px] font-semibold border cursor-pointer tabular-nums ' + (gradeAllChecked(g) ? 'bg-ink text-white border-ink' : 'bg-white text-neutral-600 border-neutral-300 hover:bg-neutral-50')}>
                  {gradeAllChecked(g) ? '✓ ' : ''}{gradeLabel(g)} · {n}
                </button>
              ))}
            </div>
          )}
          {available === null ? (
            <div className="text-[12px] text-neutral-400 py-1">Loading…</div>
          ) : available.length === 0 ? (
            <div className="text-[13px] text-neutral-500 py-1">
              {q ? 'No available students match.' : 'Every student you have is already in this class'}
            </div>
          ) : (
            <div className="max-h-[260px] overflow-y-auto border-[1px] border-neutral-200 rounded-[8px]">
              {available.map((p) => (
                <label
                  key={p.userId}
                  className="flex items-center gap-[12px] px-[12px] py-2 cursor-pointer hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0"
                >
                  <StudentRow s={p}>
                    <input
                      type="checkbox"
                      checked={checked.has(p.userId)}
                      onChange={() => toggle(p.userId)}
                      className="w-3.5 h-3.5 accent-primary-600 cursor-pointer flex-none"
                    />
                  </StudentRow>
                </label>
              ))}
            </div>
          )}
          <p className="mt-2 mb-0 text-[12px] text-neutral-500">
            Added students only get the assignments that are not yet due.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 px-[24px] py-3.5 border-t border-neutral-200 bg-neutral-50 flex-none">
        <span className="text-[12px] font-semibold text-neutral-500">
          {checked.size > 0 ? `${checked.size} selected` : ''}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>Done</Button>
          <Button variant="primary" size="sm" onClick={add} disabled={addBusy || checked.size === 0}>
            {addBusy
              ? 'Adding…'
              : checked.size > 0
                ? `Add ${checked.size} student${checked.size === 1 ? '' : 's'}`
                : 'Add students'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* Co-teachers (2026-09-12): per-class grants to other teachers at the same
 * school. A co-teacher sees and runs the class exactly like the owner; only
 * the owner can delete the class or change this list. Non-owners see the
 * list read-only (their "leave" lives in the danger row below). */
function CoTeachersSection({ cls, auth, pushToast, isOwner, onAction }) {
  const [state, setState] = useState(null); // null = loading; { coTeachers, owner }
  const [pool, setPool] = useState(null);   // same-school teachers, owner only
  const [school, setSchool] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);

  const load = async () => {
    try { setState(await listCoTeachers(auth, cls.id)); } catch { setState({ coTeachers: [], owner: null }); }
  };
  useEffect(() => {
    setState(null);
    setPool(null);
    setPickerOpen(false);
    setQuery('');
    setConfirmRemove(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cls?.id]);
  useEffect(() => {
    if (!pickerOpen || !isOwner || pool !== null) return;
    listSchoolTeachers(auth, cls.id)
      .then((r) => { setPool(r.teachers); setSchool(r.school); })
      .catch(() => { setPool([]); setSchool(null); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen]);

  const coTeachers = state?.coTeachers || [];
  const q = query.trim().toLowerCase();
  const available = (pool || [])
    .filter((t) => !coTeachers.some((c) => c.userId === t.userId))
    .filter((t) => !q || String(t.name || '').toLowerCase().includes(q) || String(t.email || '').toLowerCase().includes(q))
    .sort((a, b) => lastNameOf(a.name || '').localeCompare(lastNameOf(b.name || '')) || String(a.name || '').localeCompare(String(b.name || '')));

  const add = async (t) => {
    if (busyId) return;
    setBusyId(t.userId);
    try {
      await addCoTeacher(auth, cls.id, t.userId);
      pushToast(`${t.name || t.email} can now co-teach this class`);
      onAction?.(`${t.name || t.email} can now co-teach this class.`);
      await load();
    } catch (e) {
      pushToast(e?.viewOnly ? e.message : e?.status === 409 ? 'Already a co-teacher' : 'Could not add the co-teacher');
    } finally {
      setBusyId(null);
    }
  };
  const remove = async (t) => {
    if (busyId) return;
    setBusyId(t.userId);
    try {
      await removeCoTeacher(auth, cls.id, t.userId);
      pushToast(`${t.name || t.email} removed`);
      onAction?.(`${t.name || t.email} no longer co-teaches this class.`);
      await load();
    } catch (e) {
      pushToast(e?.viewOnly ? e.message : 'Could not remove the co-teacher');
    } finally {
      setBusyId(null);
      setConfirmRemove(null);
    }
  };

  return (
    <div className="border-t border-neutral-100 px-[24px] py-[16px]">
      <div className="flex items-center gap-2 mb-1.5">
        <div className="text-[11px] font-extrabold uppercase tracking-wide text-neutral-400">
          Co-teachers{state !== null ? ` (${coTeachers.length})` : ''}
        </div>
        <span className="flex-1" />
        {isOwner && (
          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            className="flex items-center gap-1 text-[11.5px] font-bold text-primary-600 hover:text-primary-700 bg-transparent border-0 p-0 cursor-pointer"
          >
            <UserPlus size={13} strokeWidth={2.5} />
            {pickerOpen ? 'Close' : 'Add a co-teacher'}
          </button>
        )}
      </div>

      {state === null ? (
        <div className="text-[12px] text-neutral-400 py-1">Loading…</div>
      ) : coTeachers.length === 0 ? (
        <div className="text-[13px] text-neutral-500 py-1">
          {isOwner ? 'No co-teachers yet.' : 'No other co-teachers on this class.'}
        </div>
      ) : (
        <div className="border-[1px] border-neutral-200 rounded-[8px]">
          {coTeachers.map((t) => (
            <div key={t.userId} className="flex items-center gap-[12px] px-[12px] py-2 border-b border-neutral-100 last:border-b-0">
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold text-ink truncate leading-tight">{t.name || t.email || t.userId}</span>
                {t.email && t.email !== t.name && (
                  <span className="block text-[11px] text-neutral-400 truncate leading-tight">{t.email}</span>
                )}
              </span>
              {isOwner && (
                <button
                  type="button"
                  onClick={confirmRemove === t.userId ? () => remove(t) : () => setConfirmRemove(t.userId)}
                  disabled={!!busyId}
                  className={
                    'flex-none text-[12px] font-semibold border-0 bg-transparent cursor-pointer p-0 disabled:opacity-60 disabled:cursor-default ' +
                    (confirmRemove === t.userId ? 'text-error-700' : 'text-neutral-400 hover:text-error-700')
                  }
                >
                  {busyId === t.userId ? 'Removing…' : confirmRemove === t.userId ? 'Click again to confirm' : 'Remove'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {isOwner && pickerOpen && (
        <div className="mt-2.5">
          {pool === null ? (
            <div className="text-[12px] text-neutral-400 py-1">Loading teachers…</div>
          ) : !school ? (
            <div className="text-[12px] text-neutral-500 py-1">
              Your account has no school set, so there is no teacher list to pick from.
            </div>
          ) : (
            <>
              <div className="relative mb-1.5">
                <Search size={14} strokeWidth={2.5} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`Search teachers at ${school}`}
                  className="w-full h-9 pl-8 pr-7 text-[13px] font-semibold text-ink bg-white border-[1px] border-neutral-300 rounded-[6px] outline-none focus:border-neutral-400 placeholder:font-normal placeholder:text-neutral-400"
                />
                {query && (
                  <button onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center border-none bg-transparent p-0.5 text-neutral-400 hover:text-neutral-600 cursor-pointer">
                    <X size={13} strokeWidth={2.5} />
                  </button>
                )}
              </div>
              {available.length === 0 ? (
                <div className="text-[13px] text-neutral-500 py-1">
                  {q ? 'No teachers match.' : 'Every teacher at your school already co-teaches this class.'}
                </div>
              ) : (
                <div className="max-h-[200px] overflow-y-auto border-[1px] border-neutral-200 rounded-[8px]">
                  {available.map((t) => (
                    <div key={t.userId} className="flex items-center gap-[12px] px-[12px] py-2 border-b border-neutral-100 last:border-b-0">
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-semibold text-ink truncate leading-tight">{t.name || t.email || t.userId}</span>
                        {t.email && t.email !== t.name && (
                          <span className="block text-[11px] text-neutral-400 truncate leading-tight">{t.email}</span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => add(t)}
                        disabled={!!busyId}
                        className="flex-none text-[12px] font-bold text-primary-600 hover:text-primary-700 border-0 bg-transparent cursor-pointer p-0 disabled:opacity-60 disabled:cursor-default"
                      >
                        {busyId === t.userId ? 'Adding…' : 'Add'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <p className="mt-2 mb-0 text-[12px] text-neutral-500">
        {isOwner
          ? 'Co-teachers see and run this class exactly like you do. They cannot delete it or change this list.'
          : `${state?.owner?.name || 'The owner'} owns this class and manages who co-teaches it.`}
      </p>
    </div>
  );
}

// initialSection (Buffy's manageClass action, 2026-10-13): 'students' opens
// Edit students straight away (studentQuery prefills its search), 'coteachers'
// and 'delete' scroll to that part. onAction(note) reports each change made
// here, so the chat can say what happened.
export default function ManageClassModal({ open, onClose, cls, classes, selectClass, initialSection = null, studentQuery = '', prefill = null, onAction }) {
  const { user, handleAPIResponse } = useAuth();
  const auth = useMemo(() => ({ user, handleAPIResponse }), [user, handleAPIResponse]);
  const pushToast = useToast();

  // Details
  const [name, setName] = useState('');
  const [period, setPeriod] = useState('');
  const [saving, setSaving] = useState(false);
  // Roster editor
  const [studentsOpen, setStudentsOpen] = useState(false);
  // Danger
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const coRef = useRef(null);
  const dangerRef = useRef(null);

  useEffect(() => {
    if (!open || !cls) return;
    // A rename Buffy drafted fills the fields; the teacher still presses Save.
    setName(prefill?.name || cls.name || '');
    setPeriod(prefill?.period ?? cls.period ?? '');
    setSaving(false);
    setStudentsOpen(initialSection === 'students');
    setConfirmDelete(false);
    setDeleteBusy(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cls?.id]);
  // Take the teacher to the part Buffy named (never pre-arming a delete).
  useEffect(() => {
    if (!open || !cls) return undefined;
    const target = initialSection === 'coteachers' ? coRef.current : initialSection === 'delete' ? dangerRef.current : null;
    if (!target) return undefined;
    const t = setTimeout(() => { target.scrollIntoView({ block: 'center' }); target.querySelector('button')?.focus(); }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cls?.id, initialSection]);

  if (!cls) return null;

  const save = async () => {
    if (saving || !name.trim()) return;
    setSaving(true);
    try {
      await updateClass(auth, cls.id, { name: name.trim(), period: period.trim() });
      notifyClassesChanged();
      pushToast('Class updated');
      onAction?.(`Class renamed to ${[period.trim(), name.trim()].filter(Boolean).join(' · ')}.`);
    } catch (e) {
      pushToast(e?.viewOnly ? e.message : 'Could not update the class');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (deleteBusy) return;
    setDeleteBusy(true);
    try {
      await deleteClass(auth, cls.id);
      // Clear/reselect the persisted selection so the hook never points at a
      // deleted class: hop to another class if one exists, else drop the key
      // (useSelectedClass falls back to list[0] / the first-run state).
      const next = (classes || []).find((c) => c.id !== cls.id);
      writeSelectedClassId(next ? next.id : null);
      if (next && selectClass) selectClass(next.id);
      onClose();
      notifyClassesChanged();
      pushToast('Class deleted');
      onAction?.(`Deleted the class ${cls.name}.`);
    } catch (e) {
      pushToast(e?.viewOnly ? e.message : 'Could not delete the class');
      setDeleteBusy(false);
      setConfirmDelete(false);
    }
  };

  // Co-teacher's exit: drop MY grant (the class and its records are untouched
  // for the owner and everyone else). Reselects like delete does.
  const isOwner = cls.isOwner !== false;
  const doLeave = async () => {
    if (deleteBusy) return;
    setDeleteBusy(true);
    try {
      await removeCoTeacher(auth, cls.id); // no userId = me
      const next = (classes || []).find((c) => c.id !== cls.id);
      writeSelectedClassId(next ? next.id : null);
      if (next && selectClass) selectClass(next.id);
      onClose();
      notifyClassesChanged();
      pushToast('You left the class');
      onAction?.(`Left the class ${cls.name}.`);
    } catch (e) {
      pushToast(e?.viewOnly ? e.message : 'Could not leave the class');
      setDeleteBusy(false);
      setConfirmDelete(false);
    }
  };

  return (
    <>
      {/* The manage dialog hides (stays mounted) while the roster editor is
          up, so Esc/backdrop always acts on one dialog at a time. */}
      <Modal open={open && !studentsOpen} onClose={onClose} size="md">
        <ModalHeader
          title="Manage class"
          sub={(cls.period ? cls.period + ' · ' : '') + cls.name + (!isOwner && cls.ownerName ? ` · ${cls.ownerName}'s class` : '')}
          onClose={onClose}
        />
        <div className="flex-1 overflow-y-auto">
          {/* ---- Details ---- */}
          <div className="px-[24px] py-[16px]">
            <div className="flex flex-wrap items-end gap-[12px]">
              <label className="basis-full sm:basis-0 flex-1 min-w-0 flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold text-neutral-500">Name</span>
                <input value={name} onChange={(e) => setName(e.target.value)} className={INPUT} />
              </label>
              <label className="w-[130px] flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold text-neutral-500">Period</span>
                <input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="Period 3" className={INPUT} />
              </label>
              <Button variant="secondary" size="sm" onClick={save} disabled={saving || !name.trim()}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>

          {/* ---- Students launcher ---- */}
          <div className="border-t border-neutral-100 px-[24px] py-[16px]">
            <button
              type="button"
              onClick={() => setStudentsOpen(true)}
              className="w-full flex items-center gap-[12px] px-[12px] py-2.5 rounded-[8px] border-[1px] border-neutral-200 bg-white hover:bg-neutral-50 cursor-pointer text-left"
            >
              <Users size={16} strokeWidth={2.25} className="flex-none text-neutral-500" />
              <span className="flex-1 min-w-0">
                <span className="block text-[13px] font-bold text-ink">Edit students</span>
                <span className="block text-[11.5px] text-neutral-500">
                  {cls.size != null ? `${cls.size} in this class · ` : ''}add, remove, search by name, sort by grade
                </span>
              </span>
              <span className="flex-none text-[12px] font-bold text-primary-600">Open</span>
            </button>
          </div>

          {/* ---- Co-teachers ---- */}
          <div ref={coRef}><CoTeachersSection cls={cls} auth={auth} pushToast={pushToast} isOwner={isOwner} onAction={onAction} /></div>

          {/* ---- Danger: owner deletes, a co-teacher can only leave ---- */}
          <div ref={dangerRef} className="border-t border-neutral-100 px-[24px] py-[12px]">
            {isOwner ? (
              <button
                type="button"
                onClick={confirmDelete ? doDelete : () => setConfirmDelete(true)}
                disabled={deleteBusy}
                className={
                  '-mx-[10px] w-[calc(100%+20px)] text-left px-[10px] py-2 rounded-[6px] border-none cursor-pointer hover:bg-error-50 disabled:opacity-60 ' +
                  (confirmDelete ? 'bg-error-50' : 'bg-transparent')
                }
              >
                <span className="block text-[13px] font-bold text-error-700">
                  {deleteBusy ? 'Deleting…' : confirmDelete ? 'Delete it? This removes the class for every student' : 'Delete class'}
                </span>
                <span className="block text-[11px] text-neutral-500 leading-snug">
                  {confirmDelete
                    ? 'Students keep their own work; the class and its records go away.'
                    : 'Removes the class, its roster and its records. Student work stays.'}
                </span>
              </button>
            ) : (
              <button
                type="button"
                onClick={confirmDelete ? doLeave : () => setConfirmDelete(true)}
                disabled={deleteBusy}
                className={
                  '-mx-[10px] w-[calc(100%+20px)] text-left px-[10px] py-2 rounded-[6px] border-none cursor-pointer hover:bg-error-50 disabled:opacity-60 ' +
                  (confirmDelete ? 'bg-error-50' : 'bg-transparent')
                }
              >
                <span className="block text-[13px] font-bold text-error-700">
                  {deleteBusy ? 'Leaving…' : confirmDelete ? 'Leave it? You will lose access to this class' : 'Leave class'}
                </span>
                <span className="block text-[11px] text-neutral-500 leading-snug">
                  {confirmDelete
                    ? `The class stays as it is for ${cls.ownerName || 'the owner'} and the students.`
                    : `Only ${cls.ownerName || 'the owner'} can delete this class. Leaving removes it from your list.`}
                </span>
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-[24px] py-3.5 border-t border-neutral-200 bg-neutral-50 flex-none">
          <Button variant="secondary" size="sm" onClick={onClose}>Done</Button>
        </div>
      </Modal>

      <EditStudentsModal
        open={open && studentsOpen}
        onClose={() => { setStudentsOpen(false); if (initialSection === 'students') onClose(); }}
        cls={cls}
        auth={auth}
        pushToast={pushToast}
        initialQuery={initialSection === 'students' ? studentQuery : ''}
        onAction={onAction}
      />
    </>
  );
}
